import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { AlertTriangle } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Errors are caught and user sees fallback UI
    // Future: integrate with error monitoring service (e.g., Sentry, LogRocket)
    // Development: Error details shown in UI below
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = '/';
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white flex items-center justify-center px-4">
          <div className="max-w-md w-full text-center space-y-6">
            <div className="flex justify-center">
              <div className="w-16 h-16 bg-[hsl(var(--color-1)/0.1)] rounded-full flex items-center justify-center">
                <AlertTriangle className="w-8 h-8 text-[hsl(var(--color-3))]" />
              </div>
            </div>
            
            <div className="space-y-2">
              <h1 className="text-2xl font-semibold text-neutral-900">
                Something went wrong
              </h1>
              <p className="text-neutral-700">
                We apologize for the inconvenience. An unexpected error has occurred.
              </p>
            </div>

            {import.meta.env.DEV && this.state.error && (
              <div className="bg-[hsl(var(--color-1)/0.05)] border border-[hsl(var(--color-1)/0.2)] rounded-lg p-4 text-left">
                <p className="text-sm font-mono text-[hsl(var(--color-3))] break-all">
                  {this.state.error.message}
                </p>
              </div>
            )}

            <div className="flex gap-4 justify-center">
              <Button onClick={this.handleReset} variant="default">
                Return to Home
              </Button>
              <Button 
                onClick={() => window.location.reload()} 
                variant="outline"
              >
                Reload Page
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
